/* ============================================================
 * <lion-product-gallery> — hero + thumbnails with keyboard, swipe, zoom
 * ============================================================ */

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.allSlides = [...this.querySelectorAll('.product-gallery__slide')];
    this.allThumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.stage = this.querySelector('.product-gallery__stage');
    this.enableZoom = this.dataset.enableZoom === 'true';
    this.variantImages = {};
    if (this.dataset.variantImages) {
      try { this.variantImages = JSON.parse(this.dataset.variantImages); } catch (e) {}
    }
    this.slides = this.allSlides;
    this.thumbs = this.allThumbs;
    this.activeIndex = this.slides.findIndex((s) => s.classList.contains('is-active'));
    if (this.activeIndex < 0) this.activeIndex = 0;
    this.isFiltered = false;

    // Lightbox elements
    this.lightbox = this.querySelector('[data-gallery-lightbox]');
    this.lightboxStage = this.querySelector('[data-gallery-lightbox-stage]');
    this.lightboxCounter = this.querySelector('[data-gallery-lightbox-counter]');
    this.lightboxClose = this.querySelector('[data-gallery-lightbox-close]');
    this.lightboxPrev = this.querySelector('[data-gallery-lightbox-prev]');
    this.lightboxNext = this.querySelector('[data-gallery-lightbox-next]');

    this.thumbs.forEach((t) =>
      t.addEventListener('click', () => this.selectByMediaId(t.dataset.galleryThumb))
    );
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.addEventListener('keydown', this.onKey.bind(this));
    this.setupSwipe();
    if (this.enableZoom) this.setupZoom();
    this.setupLightbox();

    // Subscribe to variant change events
    this.variantChangeUnsub = window.subscribe?.(window.PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
  }

  handleVariantChange({ variant }) {
    if (!variant || !this.variantImages) return;
    const allowedIds = this.variantImages[String(variant.id)];
    if (!allowedIds || !allowedIds.length) {
      // No metafield-driven restriction for this variant.
      // Only reset DOM if we were previously filtered — otherwise we'd
      // override the just-applied selection from lion-variant-picker's
      // swapGalleryMedia() (the native one-image-per-variant path).
      if (this.isFiltered) {
        this.resetSlides();
        this.isFiltered = false;
      }
      return;
    }
    // Filter slides and thumbs to only show allowed media
    this.slides = this.allSlides.filter((s) => allowedIds.includes(Number(s.dataset.mediaId)));
    this.thumbs = this.allThumbs.filter((t) => allowedIds.includes(Number(t.dataset.galleryThumb)));
    // Hide/show slides in DOM
    this.allSlides.forEach((s) => s.toggleAttribute('hidden', !allowedIds.includes(Number(s.dataset.mediaId))));
    this.allThumbs.forEach((t) => {
      const li = t.closest('li');
      if (li) li.hidden = !allowedIds.includes(Number(t.dataset.galleryThumb));
    });
    this.isFiltered = true;
    // Prefer variant.featured_media when it's in the allowed set; fallback to first.
    let targetIdx = 0;
    if (variant.featured_media) {
      const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(variant.featured_media.id));
      if (idx >= 0) targetIdx = idx;
    }
    if (this.slides.length) this.selectIndex(targetIdx);
  }

  resetSlides() {
    this.slides = this.allSlides;
    this.thumbs = this.allThumbs;
    this.allSlides.forEach((s) => s.removeAttribute('hidden'));
    this.allThumbs.forEach((t) => {
      const li = t.closest('li');
      if (li) li.hidden = false;
    });
    // Do NOT auto-select(0) here — caller decides which slide to focus
    // after reset (lion-variant-picker.swapGalleryMedia already chose one).
  }

  selectByMediaId(id) {
    const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
    if (idx >= 0) this.selectIndex(idx);
  }

  selectIndex(idx) {
    this.slides.forEach((s, i) => {
      s.classList.toggle('is-active', i === idx);
      s.toggleAttribute('hidden', i !== idx);
    });
    this.thumbs.forEach((t) =>
      t.classList.toggle('is-active', t.dataset.galleryThumb === this.slides[idx].dataset.mediaId)
    );
    this.activeIndex = idx;
  }

  go(delta) {
    const next = (this.activeIndex + delta + this.slides.length) % this.slides.length;
    this.selectIndex(next);
  }

  onKey(e) {
    if (e.key === 'ArrowRight') this.go(1);
    if (e.key === 'ArrowLeft') this.go(-1);
  }

  setupSwipe() {
    let startX = null;
    const threshold = 40;
    this.stage?.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    this.stage?.addEventListener('touchend', (e) => {
      if (startX == null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > threshold) this.go(dx < 0 ? 1 : -1);
      startX = null;
    });
  }

  setupZoom() {
    this.stage?.addEventListener('mousemove', (e) => {
      const active = this.slides[this.activeIndex]?.querySelector('.product-gallery__zoomable');
      if (!active) return;
      const rect = active.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      active.style.setProperty('--zoom-x', `${x}%`);
      active.style.setProperty('--zoom-y', `${y}%`);
      active.classList.add('is-zooming');
    });
    this.stage?.addEventListener('mouseleave', () => {
      this.slides.forEach((s) => s.querySelector('.product-gallery__zoomable')?.classList.remove('is-zooming'));
    });
  }

  /* Lightbox */
  setupLightbox() {
    if (!this.lightbox) return;
    // Click on active slide image opens lightbox
    this.slides.forEach((slide, i) => {
      const img = slide.querySelector('img');
      if (img) {
        img.style.cursor = 'pointer';
        img.addEventListener('click', () => {
          this.selectIndex(i);
          this.openLightbox();
        });
      }
    });
    this.lightboxClose?.addEventListener('click', () => this.closeLightbox());
    this.lightboxPrev?.addEventListener('click', (e) => { e.stopPropagation(); this.go(-1); this.updateLightbox(); });
    this.lightboxNext?.addEventListener('click', (e) => { e.stopPropagation(); this.go(1); this.updateLightbox(); });
    // Swipe in lightbox
    let lbStartX = null;
    this.lightbox.addEventListener('touchstart', (e) => { lbStartX = e.touches[0].clientX; }, { passive: true });
    this.lightbox.addEventListener('touchend', (e) => {
      if (lbStartX == null) return;
      const dx = e.changedTouches[0].clientX - lbStartX;
      if (Math.abs(dx) > 40) {
        this.go(dx < 0 ? 1 : -1);
        this.updateLightbox();
      }
      lbStartX = null;
    });
    // Click on background closes
    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox || e.target === this.lightboxStage) this.closeLightbox();
    });
  }

  openLightbox() {
    if (!this.lightbox) return;
    this.lightbox.hidden = false;
    document.body.style.overflow = 'hidden';
    this.updateLightbox();
    this.lightboxClose?.focus();
  }

  closeLightbox() {
    if (!this.lightbox) return;
    this.lightbox.hidden = true;
    document.body.style.overflow = '';
  }

  disconnectedCallback() {
    this.variantChangeUnsub?.();
  }

  updateLightbox() {
    if (!this.lightboxStage) return;
    const slide = this.slides[this.activeIndex];
    const img = slide?.querySelector('img');
    const src = img?.getAttribute('src');
    const alt = img?.getAttribute('alt') || '';
    if (src) {
      // Build via DOM API — `src` and `alt` are already escaped at the
      // server-rendered <img>, but using textContent / setAttribute
      // removes any chance of HTML injection from a future content path.
      const hi = document.createElement('img');
      hi.src = src.replace(/width=\d+/, 'width=2000').replace(/\s+/, ' ');
      hi.alt = alt;
      hi.loading = 'eager';
      this.lightboxStage.replaceChildren(hi);
    }
    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = `${this.activeIndex + 1} / ${this.slides.length}`;
    }
  }
}

if (!customElements.get('lion-product-gallery')) {
  customElements.define('lion-product-gallery', ProductGallery);
}

function initStickyProductMedia() {
  const mediaQuery = window.matchMedia('(min-width: 990px)');
  const header = document.querySelector('lion-header-component');

  document.querySelectorAll('[data-product-section][data-sticky-media="true"]').forEach((section) => {
    const sync = () => {
      if (!mediaQuery.matches) {
        section.style.removeProperty('--product-gallery-top');
        return;
      }

      if (!header) return;

      const top = Math.max(0, Math.round(header.getBoundingClientRect().bottom)) + 16;
      section.style.setProperty('--product-gallery-top', `${top}px`);
    };

    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    mediaQuery.addEventListener('change', sync);
    sync();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyProductMedia);
} else {
  initStickyProductMedia();
}
